import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const TermsConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="terms-page">
      {/* Header */}
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button 
          onClick={() => navigate(-1)}
          className="text-white hover:text-white/80 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">Terms & Conditions</h1>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Fleety – Customer Terms & Conditions</h1>
          <p className="text-sm text-gray-500 mb-6">Effective Date: 25-12-2025</p>
          
          <p className="text-gray-600 mb-6">
            These Terms & Conditions ("Terms") govern your use of the Fleety mobile application ("App", "Platform", "Fleety", "we", "us"). By registering, accessing, or placing an order on Fleety, you agree to these Terms.
          </p>

          {/* Section 1 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">1. About Fleety</h2>
            <p className="text-gray-600 mb-2">Fleety is a technology platform that enables customers to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Discover restaurants</li>
              <li>Place food orders</li>
              <li>Track order preparation and delivery</li>
            </ul>
            <p className="text-gray-600 mt-2">Fleety does not prepare food and does not own or operate restaurants.</p>
          </section>

          {/* Section 2 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">2. User Eligibility</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>You must be at least 18 years old to place orders.</li>
              <li>You agree to provide accurate and complete information during signup.</li>
              <li>You are responsible for maintaining the confidentiality of your account.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">3. Pricing & Charges</h2>
            
            <h3 className="text-base font-medium text-gray-800 mt-3 mb-2">Bill Breakdown</h3>
            <p className="text-gray-600 mb-2">Your order total includes:</p>
            
            <p className="text-gray-700 font-medium mt-3 mb-1">Food Charges:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li><strong>Item Total</strong> – Menu prices as listed by the restaurant (base price)</li>
              <li><strong>Packing Charges</strong> – the effective amount, when enabled, is shown before payment</li>
              <li><strong>GST on Food & Packing (5%)</strong> – Government tax on food items</li>
            </ul>
            
            <p className="text-gray-700 font-medium mt-3 mb-1">Service Charges:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li><strong>Delivery Fee</strong> – Distance-based delivery charges</li>
              <li><strong>Platform Fee</strong> – ₹20 service fee</li>
              <li><strong>GST on Delivery & Platform (18%)</strong> – Government tax on services</li>
            </ul>
            
            <p className="text-gray-600 mt-3">All prices displayed are final with no hidden charges.</p>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">GST Information</h3>
            <p className="text-gray-600 mb-2">Fleety complies with Indian GST regulations:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Food items attract <strong>5% GST</strong> (collected by restaurant)</li>
              <li>Delivery and platform services attract <strong>18% GST</strong> (collected by Fleety)</li>
            </ul>
            <p className="text-gray-600 mt-2">GST amounts are displayed separately in your bill for complete transparency.</p>
          </section>

          {/* Section 4 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Order Placement & Payment</h2>
            <p className="text-gray-600 mb-2">Once an order is placed on Fleety:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Payment is processed immediately.</li>
              <li>A unique Order ID is generated.</li>
              <li>The order is confirmed.</li>
              <li>Orders cannot be cancelled by the customer once placed, except where cancellation is permitted due to system errors or as required by law.</li>
              <li>A 4-digit Delivery OTP will be shared with the customer. The customer must provide this OTP to the delivery partner to confirm successful delivery. Orders cannot be marked as delivered without OTP verification.</li>
              <li>Orders may be automatically cancelled if the restaurant does not accept the order within the acceptance window or if no delivery partner is available. In such cases, a full refund will be initiated.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Refunds</h2>
            <p className="text-gray-600 mb-2">
              If an order cannot be fulfilled due to operational reasons attributable to Fleety or its platform operations, and beyond the customer's control, a full refund will be initiated to the original payment method.
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Refunds are typically credited within 5–7 business days, depending on the bank or payment provider.</li>
              <li>Fleety is not responsible for delays caused by banks or payment gateways.</li>
              <li>Refunds may also be initiated for order issues attributable to the restaurant, including missing items, incorrect quantity, incorrect, incomplete, or substandard food, in accordance with Fleety's refund and dispute policies.</li>
            </ul>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Non-Refundable Scenarios</h3>
            <p className="text-gray-600 mb-2">Refunds will not be provided in cases including, but not limited to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Orders cancelled after the food has been picked up by the delivery partner.</li>
              <li>Disputes raised after 24 hours from order completion.</li>
              <li>Incorrect or incomplete delivery address provided by the customer.</li>
              <li>Customer unavailable or unreachable at the delivery location.</li>
            </ul>
            <p className="text-gray-600 mt-2">
              To raise a dispute, customers must submit supporting evidence (such as photos or videos) through the app within 24 hours of delivery. Refunds are processed only after verification.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Food Quality & Responsibility</h2>
            <p className="text-gray-600 mb-2">Restaurants are solely responsible for:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Food quality</li>
              <li>Hygiene</li>
              <li>Preparation accuracy</li>
            </ul>
            <p className="text-gray-600 mt-2">Fleety is not liable for food quality, taste, or dietary issues.</p>
          </section>

          {/* Section 7 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">7. Delivery</h2>
            <p className="text-gray-600">
              Delivery is handled by independent delivery partners. Estimated delivery times are indicative and may vary due to traffic, weather, or operational conditions.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Ratings & Reviews</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>You may rate and review restaurants and delivery partners.</li>
              <li>Reviews must be honest and respectful.</li>
              <li>Fleety reserves the right to remove abusive or misleading content.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Misuse & Account Suspension</h2>
            <p className="text-gray-600 mb-2">Fleety may suspend or terminate accounts in cases of:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Fraudulent activity</li>
              <li>Abuse of platform features</li>
              <li>Misuse of promotions or services</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Limitation of Liability</h2>
            <p className="text-gray-600 mb-2">Fleety is not liable for:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Food quality issues</li>
              <li>Restaurant delays</li>
              <li>Indirect or consequential losses</li>
            </ul>
            <p className="text-gray-600 mt-2">Fleety's liability, if any, is limited to the order value paid.</p>
          </section>

          {/* Section 11 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Changes to Terms</h2>
            <p className="text-gray-600">
              Fleety may update these Terms from time to time. Continued use of the App constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">12. Governing Law</h2>
            <p className="text-gray-600">These Terms are governed by the laws of India.</p>
            <p className="text-gray-600">Jurisdiction: Bengaluru, Karnataka.</p>
          </section>

          {/* Section 13 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">13. Data Privacy Reference</h2>
            <p className="text-gray-600">
              Personal data is collected and processed in accordance with Fleety's Privacy Policy, available on the website.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">14. Promotions & Coupons</h2>
            <p className="text-gray-600">
              Promotional offers and coupons are subject to specific terms and may be withdrawn or cancelled in case of misuse, fraud, or violation of applicable conditions.
            </p>
          </section>

          {/* Section 15 - Account Deletion */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">15. Account Deletion</h2>
            <p className="text-gray-600 mb-2">You may request to delete your account at any time through the App:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Navigate to "My Fleety" menu and select "Delete my account"</li>
              <li>Confirm your deletion request when prompted</li>
              <li>Account deletion requests are processed within 5-7 business days</li>
              <li>You will receive confirmation once your account has been deleted</li>
            </ul>
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Account deletion is permanent and cannot be undone. Upon deletion, all your personal data, order history, saved addresses, favourite restaurants, and preferences will be permanently removed from our systems in accordance with applicable data protection laws.
              </p>
            </div>
          </section>

          {/* Section 16 */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">16. Contact</h2>
            <p className="text-gray-600 mb-3">For support or questions:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-600">
                <Mail size={18} className="text-[#3FA9F5]" />
                <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5] hover:underline">support@fleety.co.in</a>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={18} className="text-[#3FA9F5]" />
                <a href="tel:+919845331817" className="text-[#3FA9F5] hover:underline">+91-98453 31817</a>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={18} className="text-[#3FA9F5]" />
                <span>Bengaluru, India</span>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsConditionsPage;
